import { encodeFunctionData } from "viem";

type EthereumProvider = {
  isTokenPocket?: boolean;
  providers?: EthereumProvider[];
  selectedAddress?: string;
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
};

type SendContractTransactionArgs = {
  chainId: number;
  chainName?: string;
  rpcUrl?: string;
  to: `0x${string}`;
  abi: any;
  functionName: string;
  args?: unknown[];
  gas?: bigint;
};

type ChainInfo = {
  chainId: number;
  chainName: string;
  rpcUrl?: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  blockExplorerUrls?: string[];
};

function getInjectedProvider() {
  if (typeof window === "undefined") return null;
  const win = window as any;
  const ethereum = win.ethereum as EthereumProvider | undefined;
  const tokenPocketProvider = ethereum?.providers?.find(
    (item) => item.isTokenPocket
  );
  if (tokenPocketProvider) return tokenPocketProvider;
  if (ethereum?.isTokenPocket) return ethereum;
  if (win.tokenpocket?.ethereum) {
    return win.tokenpocket.ethereum as EthereumProvider;
  }
  return ethereum ?? null;
}

export function hasTokenPocketInjectedProvider() {
  if (typeof window === "undefined") return false;
  const win = window as any;
  const ethereum = win.ethereum as EthereumProvider | undefined;
  return Boolean(
    ethereum?.isTokenPocket ||
      ethereum?.providers?.some((item) => item.isTokenPocket) ||
      win.tokenpocket?.ethereum ||
      /TokenPocket/i.test(navigator.userAgent)
  );
}

function readAccounts(result: unknown) {
  return Array.isArray(result) && typeof result[0] === "string" ? result : [];
}

function readSelectedAddress(provider: EthereumProvider) {
  if (typeof provider.selectedAddress === "string" && provider.selectedAddress) {
    return provider.selectedAddress;
  }
  if (typeof window === "undefined") return "";
  const win = window as any;
  const ethereum = win.ethereum as EthereumProvider | undefined;
  if (typeof ethereum?.selectedAddress === "string" && ethereum.selectedAddress) {
    return ethereum.selectedAddress;
  }
  const tokenPocket = win.tokenpocket?.ethereum as EthereumProvider | undefined;
  if (
    typeof tokenPocket?.selectedAddress === "string" &&
    tokenPocket.selectedAddress
  ) {
    return tokenPocket.selectedAddress;
  }
  return "";
}

function isTokenPocketContextError(message: string) {
  return /private-web3-wallet-v2-get-user-storage|FragmentActivity/i.test(
    message
  );
}

function isTokenPocketProvider(provider: EthereumProvider) {
  if (typeof window === "undefined") return Boolean(provider.isTokenPocket);
  return Boolean(
    provider.isTokenPocket ||
      (window as any).tokenpocket?.ethereum === provider ||
      /TokenPocket/i.test(navigator.userAgent)
  );
}

function normalizeWalletMessage(message: string) {
  return /user.*reject|rejected.*request|user.*denied|denied.*request|cancelled|canceled/i.test(message)
    ? "Request rejected in wallet"
    : message;
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return normalizeWalletMessage(error.message);
  if (typeof error === "string") return normalizeWalletMessage(error);
  if (
    typeof error === "object" &&
    error &&
    "message" in error &&
    typeof (error as { message?: unknown }).message === "string"
  ) {
    return normalizeWalletMessage((error as { message: string }).message);
  }
  return "Wallet transaction failed";
}

export function hasInjectedProvider() {
  return getInjectedProvider() != null;
}

export async function getInjectedWalletAddress({
  requestIfNeeded = true,
}: { requestIfNeeded?: boolean } = {}) {
  const provider = getInjectedProvider();
  if (!provider) throw new Error("Please connect wallet first");

  const selectedAddress = readSelectedAddress(provider);
  if (isTokenPocketProvider(provider) && selectedAddress) {
    return selectedAddress as `0x${string}`;
  }

  const existingAccounts = readAccounts(
    await provider.request({ method: "eth_accounts" }).catch((error) => {
      const message = getErrorMessage(error);
      if (selectedAddress && isTokenPocketContextError(message)) {
        return [selectedAddress];
      }
      return [];
    })
  );
  if (existingAccounts[0]) return existingAccounts[0] as `0x${string}`;

  if (selectedAddress) return selectedAddress as `0x${string}`;

  if (!requestIfNeeded) throw new Error("Please connect wallet first");

  try {
    const requestedAccounts = readAccounts(
      await provider.request({ method: "eth_requestAccounts" })
    );
    if (requestedAccounts[0]) return requestedAccounts[0] as `0x${string}`;
  } catch (error) {
    const message = getErrorMessage(error);
    const fallbackAddress = readSelectedAddress(provider);
    if (fallbackAddress) return fallbackAddress as `0x${string}`;
    if (isTokenPocketContextError(message)) {
      throw new Error(
        "TokenPocket cannot open its wallet selector here. Please tap the alien button to sign in first, then try again."
      );
    }
    throw error;
  }

  throw new Error("Please connect wallet first");
}

async function switchInjectedChain(
  provider: EthereumProvider,
  chain: ChainInfo
) {
  const chainHex = `0x${chain.chainId.toString(16)}`;
  try {
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: chainHex }],
    });
  } catch (error) {
    const message = getErrorMessage(error);
    const code =
      typeof error === "object" && error && "code" in error
        ? Number((error as { code?: unknown }).code)
        : undefined;
    const canAddChain =
      code === 4902 || /unrecognized|unknown|not added/i.test(message);
    if (!canAddChain || !chain.rpcUrl) throw error;
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [
        {
          chainId: chainHex,
          chainName: chain.chainName,
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: [chain.rpcUrl],
          ...(chain.blockExplorerUrls
            ? { blockExplorerUrls: chain.blockExplorerUrls }
            : {}),
        },
      ],
    });
  }
}

function defaultChainInfo(
  chainId: number,
  chainName?: string,
  rpcUrl?: string
): ChainInfo {
  if (chainId === 56) {
    return {
      chainId,
      chainName: !chainName || chainName === "BSC" ? "BNB Smart Chain" : chainName,
      rpcUrl,
      nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
      blockExplorerUrls: ["https://bscscan.com"],
    };
  }
  if (chainId === 8453) {
    return {
      chainId,
      chainName: !chainName || chainName === "BASE" ? "Base" : chainName,
      rpcUrl,
      nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
      blockExplorerUrls: ["https://basescan.org"],
    };
  }
  if (chainId === 137) {
    return {
      chainId,
      chainName: !chainName || chainName === "POL" ? "Polygon" : chainName,
      rpcUrl,
      nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
      blockExplorerUrls: ["https://polygonscan.com"],
    };
  }
  return {
    chainId,
    chainName: chainName || `Chain ${chainId}`,
    rpcUrl,
    nativeCurrency: { name: "Native Token", symbol: "ETH", decimals: 18 },
  };
}

async function readInjectedChainId(provider: EthereumProvider) {
  const result = await provider.request({ method: "eth_chainId" });
  if (typeof result !== "string") return null;
  const chainId = Number.parseInt(result, 16);
  return Number.isFinite(chainId) ? chainId : null;
}

async function ensureInjectedChain(provider: EthereumProvider, chain: ChainInfo) {
  const before = await readInjectedChainId(provider).catch(() => null);
  if (before === chain.chainId) return;
  await switchInjectedChain(provider, chain);
  const after = await readInjectedChainId(provider).catch(() => null);
  if (after !== chain.chainId) {
    throw new Error(`Please switch wallet network to ${chain.chainName} and retry`);
  }
}

export async function sendInjectedContractTransaction({
  chainId,
  chainName,
  rpcUrl,
  to,
  abi,
  functionName,
  args = [],
  gas,
}: SendContractTransactionArgs) {
  const provider = getInjectedProvider();
  if (!provider) throw new Error("Please connect wallet first");

  const from = await getInjectedWalletAddress();

  await ensureInjectedChain(
    provider,
    defaultChainInfo(chainId, chainName, rpcUrl)
  );

  const data = encodeFunctionData({
    abi,
    functionName,
    args,
  });

  const hash = await provider.request({
    method: "eth_sendTransaction",
    params: [
      {
        from,
        to,
        data,
        ...(gas ? { gas: `0x${gas.toString(16)}` } : {}),
      },
    ],
  });
  if (typeof hash !== "string") {
    throw new Error("Wallet did not return a transaction hash");
  }
  return hash as `0x${string}`;
}

type WaitForInjectedReceiptOptions = {
  timeoutMs?: number;
  rpcUrl?: string;
};

async function readReceiptFromRpc(
  rpcUrl: string,
  hash: `0x${string}`
) {
  const response = await fetch(rpcUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: Date.now(),
      method: "eth_getTransactionReceipt",
      params: [hash],
    }),
  });
  const payload = await response.json();
  if (payload?.error) throw new Error(payload.error.message || "RPC error");
  return payload?.result;
}

export async function waitForInjectedReceipt(
  hash: `0x${string}`,
  options: WaitForInjectedReceiptOptions = {}
) {
  const provider = getInjectedProvider();
  if (!provider) throw new Error("Please connect wallet first");

  const timeoutMs = options.timeoutMs ?? 120000;
  const startedAt = Date.now();
  let lastError: unknown;
  while (Date.now() - startedAt < timeoutMs) {
    let receipt: unknown = null;
    try {
      receipt = await provider.request({
        method: "eth_getTransactionReceipt",
        params: [hash],
      });
    } catch (error) {
      lastError = error;
    }
    if (!receipt && options.rpcUrl) {
      try {
        receipt = await readReceiptFromRpc(options.rpcUrl, hash);
      } catch (error) {
        lastError = error;
      }
    }
    if (receipt && typeof receipt === "object") {
      const status = (receipt as { status?: string }).status;
      if (status === "0x0") throw new Error("Transaction failed");
      return receipt;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
  }
  const detail = getErrorMessage(lastError);
  throw new Error(
    detail && detail !== "Wallet transaction failed"
      ? `Transaction confirmation timed out: ${detail}`
      : "Transaction confirmation timed out"
  );
}
